import { type Dispatch, memo, type SetStateAction, useCallback, useEffect, useMemo, useRef, type FC, Fragment } from 'react'
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation'
import styled from 'styled-components'
import { Checkbox } from '../commons/inputs'
import { TypeCmsProductsByCategoryList, TypeLang } from '@/schema'
import { FieldValues, SubmitHandler, useForm, UseFormRegister } from 'react-hook-form'
import { getP18, mq, rgbAlpha } from '@/styles'
import { useFilters, useModal, useTranslator } from '@/hooks'
import { motion } from 'motion/react'
import { MODALS } from '@/context'

type Props = {
  data: TypeCmsProductsByCategoryList['filters']
  submitOnChange?: boolean
  forceResetFilters: boolean
  setForceResetFilters: Dispatch<SetStateAction<boolean>>
  fieldGroupToReset: string | null
  setFieldGroupToReset: Dispatch<SetStateAction<string | null>>
}

const getTrueValues = (formData) => {
  const trueValues = {}
  for (const group in formData) {
    const filtered = Object.entries(formData[group])
      .filter(([, value]) => value === true)
      .reduce((acc, [key]) => {

        acc[key.split('__')[1]] = true
        return acc
      }, {} as { [key: string]: boolean })

    if (Object.keys(filtered).length > 0) {
      trueValues[group] = filtered
    }
  }

  return trueValues
}

const getFields = (data: Props['data'], activeFilters, disabledFilters, register: UseFormRegister<FieldValues>, lang: TypeLang) => data.map((item, idx) => {
  const paramKeys = Object.keys(item.values)
  const disabledKeyObj = disabledFilters?.[item?.slug?.[lang]]
  const disabledCounter = disabledKeyObj ? Object.keys(disabledKeyObj)?.length : 0
  const paramsCounter = paramKeys?.length
  const allDisabled = disabledCounter > 0 && disabledCounter === paramsCounter

  return (
    <fieldset key={`${idx}-${item.id}`} data-disabled={allDisabled} data-op={item.sage_id === 'Installation' ? 'true' : 'false'}>
      <legend>{item.name}</legend>
      {paramKeys?.map((paramValue, index) => {
        // console.log('activeFilters', activeFilters)
        const isChecked = activeFilters?.[item.slug[lang]]?.[paramValue] ?? false
        const isDisabled = !isChecked && (Object.keys(activeFilters)?.length > 1 || !activeFilters?.[item.slug[lang]]) && disabledFilters?.[item.slug[lang]]?.[paramValue]

        const id = `${item.slug[lang]}__${paramValue + ''}`

        if (id === 'angle__up-down') return <Fragment key={id + '-' + idx}/>
        return <Checkbox id={id} key={`${index}-${paramValue}`} label={item.values[paramValue].name} name={`${item.slug[lang]}[__${paramValue + ''}]`} register={register} defaultChecked={isChecked} disabled={isDisabled}/>
      })}
    </fieldset>
  )
})

export const FormFilters: FC<Props> = memo(({ data, submitOnChange = false, forceResetFilters, setForceResetFilters, fieldGroupToReset, setFieldGroupToReset }) => {
  const { lang }: { lang: TypeLang } = useParams()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { isActive, toggle } = useModal(MODALS.FILTERS)
  const { replace } = useRouter()
	const { t } = useTranslator()
  const { handleSubmit, register, watch, setValue } = useForm()
  const { setActiveFilters, activeFilters, disabledFilters } = useFilters()
  const isSyncingRef = useRef(false)
  // const [selectedValues, setSelectedValues] = useState(null)
  // const watchedValues = watch()

  // const onClickValue = () => {
  // }

  const Fields = useMemo(() => getFields(data ?? [], activeFilters, disabledFilters, register, lang), [data, activeFilters, disabledFilters, register, lang])

  const updateUrl = useCallback((activeFilters) => {
    const q = searchParams.get('q')
    const params = new URLSearchParams()

    if(q?.length > 0) params.set('q', q)


    Object.keys(activeFilters).forEach((key) => {
      const values = Object.keys(activeFilters[key])?.join(',')
      params.set(key, values)
    })

    const newUrl = `${pathname}?${params.toString()}`

    replace(newUrl)
    window.history.pushState({ path: newUrl }, '', newUrl)

  }, [pathname, searchParams, replace])

  // Drives every registered checkbox back to `false` at the DOM level (uncontrolled inputs
  // aren't reliably unchecked by a bare `reset()` without defaultValues, which caused a
  // stale native `.checked` and the "click twice" bug). Guarded so `watch` doesn't auto-submit.
  const resetAllFields = useCallback(() => {
    isSyncingRef.current = true
    data?.forEach(item => Object.keys(item.values).forEach(paramValue => setValue(`${item.slug[lang]}[__${paramValue}]`, false)))
    isSyncingRef.current = false
  }, [data, lang, setValue])

  const onSubmit: SubmitHandler<unknown> = useCallback(async formData => {
    // console.log('🟢 Submit filters data', formData)
    const activeFilters = getTrueValues(formData)
    // console.log('🟢 Submit filters true values', activeFilters)
    setActiveFilters(activeFilters)
    updateUrl(activeFilters)
  }, [setActiveFilters, updateUrl])

  const onError = (errors, e) => {
    console.warn('🟡 Errors found before Submit')
    console.log(errors, e)
  }

	const onClickClear = useCallback(() => {
		resetAllFields()
		setActiveFilters({})
		updateUrl({})
	}, [resetAllFields, setActiveFilters, updateUrl])

  // Auto submit on change
  useEffect(() => {
    if (!submitOnChange) return

    const subscription = watch(() => {
      if (isSyncingRef.current) return
      handleSubmit(onSubmit)()
    })
    return () => subscription.unsubscribe()
  }, [submitOnChange, watch, handleSubmit, onSubmit])

  useEffect(() => {
    if (!fieldGroupToReset) return

    const group = data?.find(item => item.slug[lang] === fieldGroupToReset)
    if (group) {
      isSyncingRef.current = true
      Object.keys(group.values).forEach(paramValue => {
        const desired = Boolean(activeFilters?.[fieldGroupToReset]?.[paramValue])
        setValue(`${fieldGroupToReset}[__${paramValue}]`, desired)
      })
      isSyncingRef.current = false
    }

    setFieldGroupToReset(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldGroupToReset])

  useEffect(() => {
    if(forceResetFilters) {
      resetAllFields()
      setActiveFilters({})
      updateUrl({})
      setForceResetFilters(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceResetFilters])

  return (
    <Form onSubmit={handleSubmit(onSubmit, onError)} aria-disabled={!isActive}>
      {Fields}
			<div className='actions'>
				<button type='button' className='btn--apply-filters' onClick={toggle} {...(!isActive || Object.keys(activeFilters)?.length === 0) && { disabled: true }}>
					<span>
						{t('project.filters.apply_filters')}
					</span>
				</button>
				<button type='button' className='btn--clear-filters' {...(Object.keys(activeFilters)?.length === 0) && { disabled: true }} onClick={onClickClear}>
					<span>
						{t('project.filters.clear_filters')}
					</span>
				</button>
			</div>
    </Form>
  )
})

const Form = styled.form`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 70px;
  grid-column: 1 / span 6;
  justify-content: flex-start;

  ${mq.greaterThan('tablet')} {
    grid-column: 1 / span 12;
  }

  &[aria-disabled='true'] {
    pointer-events: none;
    user-select: none;

    label,
    input,
    button {
      pointer-events: none;
      user-select: none;
    }
  }

  legend {
    border: none;
    margin-bottom: 15px;
    opacity: .5;
  }

  fieldset {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    justify-content: flex-start;

		&[data-op='true'] {
			opacity: 0 !important;
			visibility: hidden !important;
			position: absolute !important;
		}

    &[data-disabled='true'] {
      display: none;
      opacity: 0;
      pointer-events: none;
    }

    label {
      margin-bottom: 0;
      padding: 0;

      > div {
        height: 10px;
        margin-top: 8px;
        width: 10px;
      }
    }
  }

  label {
    transition: all 250ms linear;

    &[data-disabled=true] {
      height: 0;
      opacity: 0;
      pointer-events: none;
    }
  }

  input {
    &[type='radio'] {
      border-bottom: none !important;
    }

    &:disabled {
      opacity: .4;
      pointer-events: none;
    }
  }

	.actions {
		align-items: center;
		bottom: 0;
		display: flex;
		flex-grow: 2;
		gap: 20px;
		justify-content: flex-end;
		padding: 30px 16px 0;
		position: sticky;
		width: 100%;


		/* SAME BLOCK BUTTONS AS IN SECTION FILTERS (needs/pending refactoring) */
		button {
      &.btn--apply-filters,
      &.btn--clear-filters {
				align-items: center;
				display: flex;
				justify-content: center;
        padding: 6px 10px;
        white-space: nowrap;

        ${mq.greaterThan('tablet')} {
          padding: 5px 15px;
        }

				span {
					${getP18()}
					margin-top: -3px;
				}

        /* &:disabled,
        &[disabled='true'] {
          opacity: 0;
          pointer-events: none;
        } */
      }

      &.btn--apply-filters {
        background-color: #717171;
        border-radius: 50px;
        color: var(--color-background);
        margin-left: auto;
        margin-right: 5px;
        position: relative;
        transform: translateX(15px);
        transition: 250ms opacity linear, 250ms background-color linear;
        width: auto;

        &:hover {
          background-color: ${rgbAlpha('--color-black', .8)};
        }
      }

      &.btn--clear-filters {
				background-color: var(--color-white);
				color: ${rgbAlpha('--color-text', .8)};
        margin-right: 2px;
        position: relative;
        transform: translateX(15px);
        transition: 250ms opacity linear, 250ms background-color linear, 250ms color linear;
        width: auto;

        &:hover {
					color: var(--color-text);
        }
      }
    }
	}
`


export const DivPane = styled(motion.div)`
`
